// @ts-nocheck

import { useEffect, useRef, useState } from 'react'

import {
  ChipButton,
  ChipButtonProps,
  Input,
  InputProps,
  Text,
} from '@hhgtech/hhg-components/mantine'
import { Missed } from '@hhgtech/icons/other'
import { Carousel, Embla } from '@mantine/carousel'
import {
  Box,
  Flex,
  Group,
  HoverCard,
  Button as MantineButton,
  Stack,
  rem,
} from '@mantine/core'
import cls from 'classnames'
import { useIntl } from 'react-intl'

import { AdditionalQuestionFieldType } from 'interfaces/types'

import ControlNext from './ControlNext'
import ControlPrev from './ControlPrev'

type Props = {
  question: AdditionalQuestionFieldType
  chipProps?: ChipButtonProps
  inputProps?: InputProps
  multiple?: boolean
  disabledOther?: boolean
  isOneLine?: boolean
  onChange?: (v: AdditionalQuestionFieldType['answers']) => void
}

export const OTHER_FIELD_KEY = 'otherQuestionField'

const AdditionalQuestionField = ({
  question,
  onChange,
  chipProps,
  inputProps,
  multiple,
  disabledOther,
  isOneLine = true,
}: Props) => {
  const lastSlideRef = useRef<HTMLDivElement | null>(null)
  const carouselRef = useRef<HTMLDivElement | null>(null)
  const { formatMessage: f } = useIntl()
  const [value, setValue] = useState<string[]>()
  const [otherText, setOtherText] = useState<string>('')
  const [embla, setEmbla] = useState<Embla | null>(null)
  const [canScrollNext, setCanScrollNext] = useState(false)
  const [canScrollPrev, setCanScrollPrev] = useState(false)

  const { hasAnother } = question

  useEffect(() => {
    if (typeof inputProps.value !== 'undefined') {
      const listSelectedVal = (inputProps?.value || '')
        .split(',')
        .filter(Boolean)
      const listQuestionAnswer = (question.answers || []).map((i) =>
        String(i.id),
      )
      const tmp = []
      const notIncludeList = []
      listSelectedVal.map((i) => {
        if (listQuestionAnswer.includes(i)) {
          tmp.push(i)
        } else {
          notIncludeList.push(i)
        }
      })
      const hasOtherValue = question?.hasAnother && notIncludeList.length > 0
      setValue([...tmp, ...(hasOtherValue ? [OTHER_FIELD_KEY] : [])])
      hasOtherValue && setOtherText(notIncludeList?.[0] || '')
    }
  }, [inputProps?.value])

  const reformatChipBtnValue = (value: string[]) => {
    return multiple ? value : value?.[0] || ''
  }

  const formatCustomValue = (value: string | string[]) => {
    return multiple ? (value as string) : [value]
  }

  const handleBtnClick = (i: string | string) => {
    if (!multiple && value?.[0] === i) {
      setValue('')
      onChange?.([])
    }
  }

  const handleOnchange = ({
    value,
    otherText = '',
  }: {
    value: string[]
    otherText: string
  }) => {
    const otherIdx = value.findIndex((i) => i === OTHER_FIELD_KEY)
    if (otherIdx !== -1) {
      // EXIST OTHER KEY
      const newVal = [...value]
      if (otherText) {
        newVal[otherIdx] = otherText
      }
      return newVal
    }
    return value
  }

  useEffect(() => {
    if (embla) {
      const handler = () => {
        setCanScrollNext(embla.canScrollNext())
        setCanScrollPrev(embla.canScrollPrev())
      }
      embla.on('scroll', handler)
      handler()

      return () => {
        embla.off('scroll', handler)
      }
    }
  }, [embla])

  const renderedContent = isOneLine ? (
    <Box
      className={cls('min-w-0 w-full relative', {
        'pr-5': canScrollNext,
      })}
      ref={carouselRef}
    >
      <ControlPrev
        canScrollPrev={canScrollPrev}
        onClick={() => embla?.scrollPrev()}
      />
      <Carousel
        align="start"
        containScroll="trimSnaps"
        dragFree={canScrollNext || canScrollPrev}
        draggable={canScrollNext || canScrollPrev}
        slideGap={8}
        slideSize="auto"
        withControls={false}
        withIndicators={false}
        getEmblaApi={setEmbla}
      >
        {question.answers.map((i, idx) => (
          <Carousel.Slide key={String(idx)}>
            <ChipButton
              key={String(idx)}
              value={String(i.id)}
              onClick={() => handleBtnClick(String(i.id))}
            >
              {i.answer}
            </ChipButton>
            {idx === question.answers.length - 1 && (
              <div className="absolute top-0 right-0" ref={lastSlideRef} />
            )}
          </Carousel.Slide>
        ))}
        {hasAnother && !disabledOther && (
          <Carousel.Slide key="hasAnother">
            <ChipButton
              value={OTHER_FIELD_KEY}
              onClick={() => handleBtnClick(OTHER_FIELD_KEY)}
            >
              {f({
                id: 'additionalQuestion.otherLabel',
                defaultMessage: 'Other ...',
              })}
            </ChipButton>
          </Carousel.Slide>
        )}
      </Carousel>
      <ControlNext
        canScrollNext={canScrollNext}
        onClick={() => embla?.scrollNext()}
      />
    </Box>
  ) : (
    <>
      {question.answers.map((i, idx) => (
        <ChipButton
          key={String(idx)}
          value={String(i.id)}
          onClick={() => handleBtnClick(String(i.id))}
        >
          {i.answer}
        </ChipButton>
      ))}
      {hasAnother && !disabledOther && (
        <ChipButton
          value={OTHER_FIELD_KEY}
          onClick={() => handleBtnClick(OTHER_FIELD_KEY)}
        >
          {f({
            id: 'additionalQuestion.otherLabel',
            defaultMessage: 'Other ...',
          })}
        </ChipButton>
      )}
    </>
  )

  return (
    <Stack spacing={rem(8)}>
      <Stack spacing={rem(2)}>
        <Flex align={'center'} gap={rem(8)}>
          <Text as="label" size="s4">
            {question.question.question}
          </Text>
          {question.question.questionHint && (
            <Box sx={{ position: 'relative' }}>
              <HoverCard
                withArrow
                withinPortal
                styles={(theme) => ({
                  dropdown: {
                    background: theme.colors.gray[8],
                    maxWidth: rem(300),
                  },
                })}
              >
                <HoverCard.Target>
                  <MantineButton
                    styles={{ root: { padding: 0, height: 'auto' } }}
                  >
                    <Missed size={18} />
                  </MantineButton>
                </HoverCard.Target>
                <HoverCard.Dropdown>
                  <Text size="n1" color="#ffffff">
                    {question.question.questionHint}
                  </Text>{' '}
                </HoverCard.Dropdown>
              </HoverCard>
            </Box>
          )}
        </Flex>
        <ChipButton.Group
          multiple={multiple}
          value={reformatChipBtnValue(value)}
          {...(chipProps || {})}
          onChange={(v: string | string[]) => {
            const newVal = formatCustomValue(v)
            setValue(newVal)
            onChange?.(handleOnchange({ value: newVal, otherText }))
          }}
        >
          <Group spacing={rem(8)}>{renderedContent}</Group>
        </ChipButton.Group>
        <Input type="hidden" {...inputProps} />
      </Stack>
      {(value || []).includes(OTHER_FIELD_KEY) && !disabledOther && (
        <Input
          value={otherText === OTHER_FIELD_KEY ? '' : otherText}
          placeholder={question.otherInputPlaceholder}
          onChange={(v = '') => {
            setOtherText(v)
            onChange?.(handleOnchange({ value, otherText: v }))
          }}
        />
      )}
    </Stack>
  )
}
export { AdditionalQuestionField }
